import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, Search, X } from 'lucide-react';
import { Car } from '../types';
import './CarSelectionTable.css';

interface CarWithConfig extends Car {
  ballast: number;
  restrictor: number;
  selected: boolean;
}

interface CarSelectionTableProps {
  cars: Car[];
  selectedCars: string[];
  carConfigs: Map<string, { ballast: number; restrictor: number }>;
  onSelectionChange: (carName: string, selected: boolean) => void;
  onConfigChange: (carName: string, ballast: number, restrictor: number) => void;
}

type SortField = 'name' | 'performance' | 'ballast' | 'restrictor';
type SortDirection = 'asc' | 'desc';

const PROPERTY_1_LABELS: { [key: number]: string } = {
  0: 'Road',
  1: 'Race',
  2: 'Track',
};

const PROPERTY_2_LABELS: { [key: number]: string } = {
  0: 'Modern',
  1: 'Vintage',
  2: 'Youngtimer',
};

const PROPERTY_3_LABELS: { [key: number]: string } = {
  0: 'ICE',
  1: 'EV',
  2: 'Hybrid',
};

const CarSelectionTable: React.FC<CarSelectionTableProps> = ({
  cars,
  selectedCars,
  carConfigs,
  onSelectionChange,
  onConfigChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [property1Filter, setProperty1Filter] = useState<Set<number>>(new Set());
  const [property2Filter, setProperty2Filter] = useState<Set<number>>(new Set());
  const [property3Filter, setProperty3Filter] = useState<Set<number>>(new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  // Prepare car data with config
  const carsWithConfig: CarWithConfig[] = useMemo(
    () =>
      cars.map((car) => ({
        ...car,
        selected: selectedCars.includes(car.name),
        ballast: carConfigs.get(car.name)?.ballast || 0,
        restrictor: carConfigs.get(car.name)?.restrictor || 0,
      })),
    [cars, selectedCars, carConfigs]
  );

  // Filter cars by properties and search
  const filteredCars = useMemo(
    () =>
      carsWithConfig.filter((car) => {
        const matchesSearch =
          car.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          car.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesShowSelected = !showOnlySelected || car.selected;

        // Property filters: AND between properties, OR within a property
        const matchesProperty1 =
          property1Filter.size === 0 || property1Filter.has(car.property_1 ?? 0);
        const matchesProperty2 =
          property2Filter.size === 0 || property2Filter.has(car.property_2 ?? 0);
        const matchesProperty3 =
          property3Filter.size === 0 || property3Filter.has(car.property_3 ?? 0);

        return (
          matchesSearch &&
          matchesShowSelected &&
          matchesProperty1 &&
          matchesProperty2 &&
          matchesProperty3
        );
      }),
    [carsWithConfig, searchTerm, showOnlySelected, property1Filter, property2Filter, property3Filter]
  );

  // Sort cars
  const sortedCars = useMemo(
    () => {
      const sorted = [...filteredCars];
      sorted.sort((a, b) => {
        let aVal: any;
        let bVal: any;

        switch (sortField) {
          case 'name':
            aVal = a.display_name;
            bVal = b.display_name;
            break;
          case 'performance':
            aVal = a.performance_indicator || 0;
            bVal = b.performance_indicator || 0;
            break;
          case 'ballast':
            aVal = a.ballast;
            bVal = b.ballast;
            break;
          case 'restrictor':
            aVal = a.restrictor;
            bVal = b.restrictor;
            break;
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    },
    [filteredCars, sortField, sortDirection]
  );

  // Update indeterminate state for select-all checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate =
        sortedCars.some((car) => car.selected) &&
        !sortedCars.every((car) => car.selected);
    }
  }, [sortedCars]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const togglePropertyFilter = (
    property: number,
    value: number,
    setter: React.Dispatch<React.SetStateAction<Set<number>>>
  ) => {
    setter((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return newSet;
    });
  };

  const clearAllFilters = () => {
    setProperty1Filter(new Set());
    setProperty2Filter(new Set());
    setProperty3Filter(new Set());
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp size={16} className="sort-icon" />
    ) : (
      <ChevronDown size={16} className="sort-icon" />
    );
  };

  const hasActiveFilters = property1Filter.size > 0 || property2Filter.size > 0 || property3Filter.size > 0;

  return (
    <div className="car-selection-table">
      <div className="table-toolbar">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search cars..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <label className="show-selected-checkbox">
          <input
            type="checkbox"
            checked={showOnlySelected}
            onChange={(e) => setShowOnlySelected(e.target.checked)}
          />
          <span>Show only selected</span>
        </label>
      </div>

      {/* Property Filters */}
      <div className="property-filters">
        <div className="filter-group">
          <label className="filter-label">Type:</label>
          <div className="filter-options">
            {Object.entries(PROPERTY_1_LABELS).map(([value, label]) => (
              <button
                key={value}
                className={`filter-button ${property1Filter.has(parseInt(value)) ? 'active' : ''}`}
                onClick={() => togglePropertyFilter(1, parseInt(value), setProperty1Filter)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <label className="filter-label">Era:</label>
          <div className="filter-options">
            {Object.entries(PROPERTY_2_LABELS).map(([value, label]) => (
              <button
                key={value}
                className={`filter-button ${property2Filter.has(parseInt(value)) ? 'active' : ''}`}
                onClick={() => togglePropertyFilter(2, parseInt(value), setProperty2Filter)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <label className="filter-label">Powertrain:</label>
          <div className="filter-options">
            {Object.entries(PROPERTY_3_LABELS).map(([value, label]) => (
              <button
                key={value}
                className={`filter-button ${property3Filter.has(parseInt(value)) ? 'active' : ''}`}
                onClick={() => togglePropertyFilter(3, parseInt(value), setProperty3Filter)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {hasActiveFilters && (
          <button className="clear-filters-btn" onClick={clearAllFilters}>
            <X size={16} />
            Clear filters
          </button>
        )}
      </div>

      <div className="table-wrapper">
        <table className="cars-table">
          <thead>
            <tr>
              <th className="col-checkbox">
                <input
                  ref={selectAllCheckboxRef}
                  type="checkbox"
                  checked={
                    sortedCars.length > 0 &&
                    sortedCars.every((car) => car.selected)
                  }
                  onChange={(e) => {
                    sortedCars.forEach((car) => {
                      onSelectionChange(car.name, e.target.checked);
                    });
                  }}
                />
              </th>
              <th
                className="col-name"
                onClick={() => handleSort('name')}
              >
                <div className="header-content">
                  Car Name
                  <SortIcon field="name" />
                </div>
              </th>
              <th
                className="col-performance"
                onClick={() => handleSort('performance')}
              >
                <div className="header-content">
                  Performance Index
                  <SortIcon field="performance" />
                </div>
              </th>
              <th
                className="col-ballast"
                onClick={() => handleSort('ballast')}
              >
                <div className="header-content">
                  Ballast (kg)
                  <SortIcon field="ballast" />
                </div>
              </th>
              <th
                className="col-restrictor"
                onClick={() => handleSort('restrictor')}
              >
                <div className="header-content">
                  Restrictor (%)
                  <SortIcon field="restrictor" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedCars.length > 0 ? (
              sortedCars.map((car) => (
                <tr
                  key={car.name}
                  className={`car-row ${car.selected ? 'selected' : ''}`}
                >
                  <td className="col-checkbox">
                    <input
                      type="checkbox"
                      checked={car.selected}
                      onChange={(e) =>
                        onSelectionChange(car.name, e.target.checked)
                      }
                    />
                  </td>
                  <td className="col-name">{car.display_name}</td>
                  <td className="col-performance">
                    {car.performance_indicator.toFixed(2) || 'N/A'}
                  </td>
                  <td className="col-ballast">
                    {car.selected ? (
                      <input
                        type="number"
                        value={car.ballast}
                        onChange={(e) =>
                          onConfigChange(
                            car.name,
                            parseInt(e.target.value) || 0,
                            car.restrictor
                          )
                        }
                        min="0"
                        max="1000"
                        className="config-input"
                      />
                    ) : (
                      <span className="disabled-value">{car.ballast}</span>
                    )}
                  </td>
                  <td className="col-restrictor">
                    {car.selected ? (
                      <input
                        type="number"
                        value={car.restrictor.toFixed(1)}
                        onChange={(e) =>
                          onConfigChange(
                            car.name,
                            car.ballast,
                            parseFloat(e.target.value) || 0
                          )
                        }
                        min="0"
                        max="100"
                        step="0.1"
                        className="config-input"
                      />
                    ) : (
                      <span className="disabled-value">
                        {car.restrictor.toFixed(1)}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr className="no-results">
                <td colSpan={5}>No cars found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <p className="cars-count">
          Selected: {selectedCars.length} / {cars.length}
        </p>
      </div>
    </div>
  );
};

export default CarSelectionTable;
